import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Chip,
  Stack,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Select,
  MenuItem,
} from '@mui/material';
import { format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';

// Custom Time Input component with controlled AM/PM order
interface TimeInputProps {
  value: Date | null;
  onChange: (hours: number, minutes: number) => void;
  disabled?: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, disabled }) => {
  const hours24 = value?.getHours() ?? 0;
  const minutes = value?.getMinutes() ?? 0;
  const isPM = hours24 >= 12;
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;

  const handleHourChange = (newHour12: number) => {
    let newHour24 = newHour12;
    if (isPM) {
      newHour24 = newHour12 === 12 ? 12 : newHour12 + 12;
    } else {
      newHour24 = newHour12 === 12 ? 0 : newHour12;
    }
    onChange(newHour24, minutes);
  };

  const handleMinuteChange = (newMinute: number) => {
    onChange(hours24, newMinute);
  };

  const handlePeriodChange = (newPeriod: 'AM' | 'PM') => {
    let newHour24 = hours24;
    if (newPeriod === 'AM' && isPM) {
      newHour24 = hours24 === 12 ? 0 : hours24 - 12;
    } else if (newPeriod === 'PM' && !isPM) {
      newHour24 = hours24 === 0 ? 12 : hours24 + 12;
    }
    onChange(newHour24, minutes);
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Select
        size="small"
        value={hours12}
        onChange={(e) => handleHourChange(Number(e.target.value))}
        disabled={disabled}
        sx={{ minWidth: 60 }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
      >
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
          <MenuItem key={h} value={h}>{h.toString().padStart(2, '0')}</MenuItem>
        ))}
      </Select>
      <Typography>:</Typography>
      <Select
        size="small"
        value={minutes}
        onChange={(e) => handleMinuteChange(Number(e.target.value))}
        disabled={disabled}
        sx={{ minWidth: 60 }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
      >
        {Array.from({ length: 60 }, (_, i) => (
          <MenuItem key={i} value={i}>{i.toString().padStart(2, '0')}</MenuItem>
        ))}
      </Select>
      <Select
        size="small"
        value={isPM ? 'PM' : 'AM'}
        onChange={(e) => handlePeriodChange(e.target.value as 'AM' | 'PM')}
        disabled={disabled}
        sx={{ minWidth: 65 }}
      >
        <MenuItem value="AM">AM</MenuItem>
        <MenuItem value="PM">PM</MenuItem>
      </Select>
    </Box>
  );
};

interface DateTimeRangePickerProps {
  open: boolean;
  startDate: Date | null;
  endDate: Date | null;
  onClose: () => void;
  onApply: (range: { start: Date | null; end: Date | null }) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  open,
  startDate: initialStartDate,
  endDate: initialEndDate,
  onClose,
  onApply,
  minDate,
  maxDate,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  
  const startDateInputRef = useRef<HTMLInputElement | null>(null);
  const endDateInputRef = useRef<HTMLInputElement | null>(null);

  // Update local state when dialog opens with new props
  React.useEffect(() => {
    if (open) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
    }
  }, [open, initialStartDate, initialEndDate]);

  const openNativePicker = useCallback((ref: React.RefObject<HTMLInputElement | null>) => {
    const el = ref.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!el) return;
    el.focus();
    el.showPicker?.();
  }, []);

  const handleStartDateChange = (dateStr: string) => {
    if (!dateStr) {
      setStartDate(null);
      return;
    }
    
    // Parse date string manually to avoid timezone issues
    // dateStr format is "YYYY-MM-DD" from the date input
    const [year, month, day] = dateStr.split('-').map(Number);
    const newDate = new Date(year, month - 1, day); // month is 0-indexed
    
    // Preserve existing time if there was one, otherwise set to start of day
    if (startDate) {
      newDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
    } else {
      newDate.setHours(0, 0, 0, 0);
    }
    setStartDate(newDate);
  };

  const handleEndDateChange = (dateStr: string) => {
    if (!dateStr) {
      setEndDate(null);
      return;
    }
    
    // Parse date string manually to avoid timezone issues
    // dateStr format is "YYYY-MM-DD" from the date input
    const [year, month, day] = dateStr.split('-').map(Number);
    const newDate = new Date(year, month - 1, day); // month is 0-indexed
    
    // Preserve existing time if there was one, otherwise set to end of day
    if (endDate) {
      newDate.setHours(endDate.getHours(), endDate.getMinutes(), 59, 999);
    } else {
      newDate.setHours(23, 59, 59, 999);
    }
    setEndDate(newDate);
  };

  const setQuickRange = (hours: number) => {
    // Use maxDate if provided, otherwise use current date
    const end = maxDate || new Date();
    const start = new Date(end.getTime() - (hours * 60 * 60 * 1000));
    
    // Clamp to min/max boundaries if provided
    const clampedStart = minDate && start < minDate ? minDate : start;
    const clampedEnd = maxDate && end > maxDate ? maxDate : end;
    
    setStartDate(clampedStart);
    setEndDate(clampedEnd);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleApply = () => {
    onApply({ start: startDate, end: endDate });
    onClose();
  };

  const isValidRange = startDate && endDate && startDate <= endDate;

  // Calculate hours difference to detect which quick range is selected
  const getSelectedQuickHours = (): number | null => {
    if (!startDate || !endDate) return null;
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    // Check if it matches one of our quick options (with small tolerance for rounding)
    const quickOptions = [1, 4, 12, 24, 48, 72, 168];
    for (const hours of quickOptions) {
      if (Math.abs(diffHours - hours) <= 1) return hours;
    }
    return null;
  };
  
  const selectedQuickHours = getSelectedQuickHours();

  // Format date for input (YYYY-MM-DD)
  const formatDateInput = (date: Date | null) => {
    if (!date) return '';
    // Format in local timezone to avoid date shifting
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format display text for selected range
  const getSelectedRangeText = () => {
    if (!startDate && !endDate) return '';
    
    if (startDate && endDate) {
      const startStr = format(startDate, 'MMM d, yyyy');
      const endStr = format(endDate, 'MMM d, yyyy');
      return `${startStr} - ${endStr}`;
    }
    
    if (startDate) return format(startDate, 'MMM d, yyyy') + ' - ?';
    if (endDate) return '? - ' + format(endDate, 'MMM d, yyyy');
    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            Select Date Range
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Display Course Date Range Constraint */}
        {(minDate || maxDate) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {minDate && maxDate ? (
                <>Available range: {format(minDate, 'MMM d, yyyy')} - {format(maxDate, 'MMM d, yyyy')}</>
              ) : minDate ? (
                <>Dates must be after {format(minDate, 'MMM d, yyyy')}</>
              ) : (
                <>Dates must be before {format(maxDate!, 'MMM d, yyyy')}</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Display Selected Range */}
        {getSelectedRangeText() && (
          <Alert 
            severity="info" 
            sx={{ mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}
            icon={false}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleClear}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {getSelectedRangeText()}
            </Typography>
          </Alert>
        )}

        {/* Quick Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Quick Select
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[
              { label: '1 hr', hours: 1 },
              { label: '4 hrs', hours: 4 },
              { label: '12 hrs', hours: 12 },
              { label: '24 hrs', hours: 24 },
              { label: '48 hrs', hours: 48 },
              { label: '72 hrs', hours: 72 },
              { label: '7 Days', hours: 168 },
            ].map((opt) => (
              <Chip
                key={opt.label}
                label={opt.label}
                onClick={() => setQuickRange(opt.hours)}
                variant={selectedQuickHours === opt.hours ? 'filled' : 'outlined'}
                color="primary"
                size="medium"
              />
            ))}
          </Stack>
        </Box>

        {/* Start Date & Time */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Start Date
            </Typography>
          </Grid>
          <Grid item xs={7}>
            <FormControl fullWidth>
              <TextField
                type="date"
                value={formatDateInput(startDate)}
                onChange={(e) => handleStartDateChange(e.target.value)}
                inputRef={startDateInputRef}
                fullWidth
                placeholder="Start Date"
                InputProps={{
                  onClick: () => openNativePicker(startDateInputRef),
                  sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
                }}
                inputProps={{
                  min: minDate ? formatDateInput(minDate) : undefined,
                  max: endDate ? formatDateInput(endDate) : (maxDate ? formatDateInput(maxDate) : formatDateInput(new Date()))
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <TimeInput
              value={startDate}
              onChange={(hours, minutes) => {
                if (!startDate) return;
                const newDate = new Date(startDate);
                newDate.setHours(hours, minutes, 0, 0);
                setStartDate(newDate);
              }}
              disabled={!startDate}
            />
          </Grid>
        </Grid>

        {/* End Date & Time */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              End Date
            </Typography>
          </Grid>
          <Grid item xs={7}>
            <FormControl fullWidth>
              <TextField
                type="date"
                value={formatDateInput(endDate)}
                onChange={(e) => handleEndDateChange(e.target.value)}
                inputRef={endDateInputRef}
                fullWidth
                placeholder="End Date"
                InputProps={{
                  onClick: () => openNativePicker(endDateInputRef),
                  sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
                }}
                inputProps={{
                  min: startDate ? formatDateInput(startDate) : (minDate ? formatDateInput(minDate) : undefined),
                  max: maxDate ? formatDateInput(maxDate) : formatDateInput(new Date())
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <TimeInput
              value={endDate}
              onChange={(hours, minutes) => {
                if (!endDate) return;
                const newDate = new Date(endDate);
                newDate.setHours(hours, minutes, 59, 999);
                setEndDate(newDate);
              }}
              disabled={!endDate}
            />
          </Grid>
        </Grid>

        {/* Range Validation */}
        {startDate && endDate && startDate > endDate && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            Start date/time must be before end date/time.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClear} color="inherit">
          Clear
        </Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          color="primary"
          disabled={!isValidRange}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateTimeRangePicker;

